import {BrandFont, brandFontFamily, cleanColors, isBrandFont, mix, parseBrand, readableOn, VenueBrand} from './branding'

// The venue's brand (Settings → Branding in the admin; MySportia's Website → Branding) as overrides of the
// app theme's colours. ONLY the slots the venue set are returned, so an unbranded venue gets `{}` and the
// theme object it already has — nothing changes for it.
//
// What each slot paints here: primary → `primary` (every button, selected state and price in the app), `sport`,
// and the two tints derived from it; text on primary → `text-contrast`; surface → `bg` (cards); text →
// `text-default`/`text-primary`, with `text-secondary` the same ink carried 38% toward the card. Accent has no
// token in this app (one brand colour does both jobs), so it is not read. Background is the page, painted by
// the layout.

const toRgba = (hex: string, alpha: number) => {
  const n = parseInt(hex.slice(1), 16)
  // eslint-disable-next-line no-bitwise
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}

export const brandThemeColors = (raw: unknown): Record<string, string> => {
  const c = cleanColors(parseBrand(raw).brandColors)
  const out: Record<string, string> = {}
  if (c.primary) {
    out.primary = c.primary
    out.sport = c.primary
    out['primary-secondary'] = toRgba(c.primary, 0.14)
    out['sport-bg'] = toRgba(c.primary, 0.1)
  }
  const onPrimary = c.textOnPrimary ?? (c.primary ? readableOn(c.primary) : undefined)
  if (onPrimary) {
    out['text-contrast'] = onPrimary
  }
  if (c.surface) {
    out.bg = c.surface
  }
  if (c.text) {
    out['text-default'] = c.text
    out['text-primary'] = c.text
    out['text-secondary'] = mix(c.text, c.surface ?? '#ffffff', 0.38)
  }
  return out
}

/** The page colour behind the venue's pages, when the venue chose one. */
export const brandPageBackground = (raw: unknown): string | null =>
  cleanColors(parseBrand(raw).brandColors).background ?? null

export const brandFontOf = (raw: unknown): BrandFont | null => {
  const font = parseBrand(raw).brandFont
  return isBrandFont(font) ? font : null
}

export const brandFontStack = (font: BrandFont | null): string | undefined => (font ? brandFontFamily(font) : undefined)

export type {VenueBrand}
